import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { 
  ClipboardCheck, 
  MapPin, 
  History, 
  CheckCircle, 
  Check, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Loader2, 
  PackageOpen, 
  ChevronDown, 
  AlertCircle,
  X
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { updateInventoryItem, addTransaction } from '../../state/store.js';
import { saveLocalData } from '../../state/storage.js';
import { syncAfterLocalChange } from '../../services/sync.js';
import { getAuthenticatedOperatorName } from '../../services/auth.js';
import { formatDateThai, formatExpiryDate, getExpiryStatus } from '../../utils/date.js';
import { formatBoxContentsSummary } from '../../utils/box-contents.js';
import { compareProductNameEnThenTh } from '../../utils/sorting.js';

export function AuditView() {
  const inventory = useStore(s => s.inventory) || [];
  const stockAudits = useStore(s => s.stockAudits) || [];
  const settings = useStore(s => s.settings) || {};

  const [selectedLocation, setSelectedLocation] = useState('all');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Audit state
  const [counts, setCounts] = useState({}); // itemId -> number
  const [draftStatus, setDraftStatus] = useState('พร้อมตรวจนับ');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const debounceTimerRef = useRef(null);

  // Available locations
  const locations = useMemo(() => {
    const locSet = new Set();
    inventory.forEach(i => {
      if (!i.deleted && i.location?.trim()) locSet.add(i.location.trim());
    });
    return Array.from(locSet).sort((a, b) => a.localeCompare(b, 'th'));
  }, [inventory]);

  // Active items for audit (filtered by location and sorted)
  const auditItems = useMemo(() => {
    let list = inventory.filter(i => !i.deleted);
    if (selectedLocation !== 'all') {
      list = list.filter(i => i.location === selectedLocation);
    }

    return list.sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'name') {
        cmp = compareProductNameEnThenTh(a.name, b.name);
      } else if (sortColumn === 'brand') {
        cmp = compareProductNameEnThenTh(a.brand || '', b.brand || '');
        if (cmp === 0) cmp = compareProductNameEnThenTh(a.name, b.name);
      } else if (sortColumn === 'size') {
        cmp = compareProductNameEnThenTh(a.size || '', b.size || '');
        if (cmp === 0) cmp = compareProductNameEnThenTh(a.name, b.name);
      } else if (sortColumn === 'quantity') {
        cmp = (Number(a.quantity) || 0) - (Number(b.quantity) || 0);
      } else if (sortColumn === 'diff') {
        const sysA = Number(a.quantity) || 0;
        const physA = counts[a.id] !== undefined ? counts[a.id] : sysA;
        const diffA = physA - sysA;

        const sysB = Number(b.quantity) || 0;
        const physB = counts[b.id] !== undefined ? counts[b.id] : sysB;
        const diffB = physB - sysB;

        cmp = diffA - diffB;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [inventory, selectedLocation, sortColumn, sortDirection, counts]);

  // Restore counts from localStorage or initialize with existing quantity
  useEffect(() => {
    let savedCounts = {};
    try {
      const raw = localStorage.getItem('sdr_stock_audit_draft_counts');
      if (raw) savedCounts = JSON.parse(raw);
    } catch (e) {}

    const initialCounts = { ...savedCounts };
    auditItems.forEach(i => {
      if (initialCounts[i.id] === undefined) {
        initialCounts[i.id] = Number(i.quantity) || 0;
      }
    });

    setCounts(prev => ({ ...initialCounts, ...prev }));
  }, [auditItems]);

  // Handle single item count change with debounced auto-save
  const handleCountChange = useCallback((itemId, val) => {
    const num = val === '' ? 0 : Math.max(0, parseFloat(val) || 0);
    setCounts(prev => {
      const next = { ...prev, [itemId]: num };
      try {
        localStorage.setItem('sdr_stock_audit_draft_counts', JSON.stringify(next));
      } catch (e) {}
      return next;
    });

    setDraftStatus('กำลังบันทึกแบบร่าง...');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      saveLocalData();
      setDraftStatus('✓ บันทึกแบบร่างล่าสุดแล้ว');
    }, 800);
  }, []);

  // Quick match single row
  const handleQuickMatch = useCallback((itemId, systemQty) => {
    handleCountChange(itemId, systemQty);
  }, [handleCountChange]);

  // Discrepancy stats
  const stats = useMemo(() => {
    let diffCount = 0;
    let matchCount = 0;

    auditItems.forEach(item => {
      const sys = Number(item.quantity) || 0;
      const phys = counts[item.id] !== undefined ? counts[item.id] : sys;
      if (Math.abs(phys - sys) > 0.0001) {
        diffCount++;
      } else {
        matchCount++;
      }
    });

    return { diffCount, matchCount };
  }, [auditItems, counts]);

  // Sorting header click
  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (col) => {
    if (sortColumn !== col) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 ml-1" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-indigo-600 ml-1" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-indigo-600 ml-1" />
    );
  };

  // Submit and apply audit
  const handleApplyAudit = async () => {
    if (auditItems.length === 0) return;

    setSaving(true);
    setErrorMsg('');

    try {
      const nowIso = new Date().toISOString();
      const operator = getAuthenticatedOperatorName() || 'เจ้าหน้าที่ตรวจนับ';
      const auditId = `audit_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

      const auditRecords = [];
      let totalDiscrepancies = 0;

      for (let i = 0; i < auditItems.length; i++) {
        const item = auditItems[i];
        const physical = counts[item.id] !== undefined ? counts[item.id] : (Number(item.quantity) || 0);
        const system = Number(item.quantity) || 0;
        const diff = Number((physical - system).toFixed(5));

        if (diff !== 0) {
          totalDiscrepancies++;
          // Record adjustment transaction
          addTransaction({
            id: `tx_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
            itemId: item.id,
            itemName: item.name,
            type: 'adjust',
            quantity: Math.abs(diff),
            unit: item.unit || 'ชิ้น',
            borrowerName: `ปรับยอดตรวจนับ (${operator})`,
            notes: `ยอดระบบ: ${system} -> ตรวจนับจริง: ${physical} (ผลต่าง: ${diff > 0 ? '+' : ''}${diff})`,
            timestamp: nowIso,
            operatorEmail: operator,
            deleted: false
          });

          // Update inventory item quantity
          updateInventoryItem({
            ...item,
            quantity: physical,
            updatedAt: nowIso,
            updatedBy: operator
          });
        }

        auditRecords.push({
          itemId: item.id,
          itemName: item.name,
          lot: item.lot || 'ล็อตหลัก',
          systemQty: system,
          physicalQty: physical,
          discrepancy: diff
        });
      }

      // Save to stockAudits history in store
      const auditEntry = {
        id: auditId,
        date: nowIso,
        operator: operator,
        location: selectedLocation,
        recordsCount: auditItems.length,
        discrepanciesCount: totalDiscrepancies,
        records: auditRecords,
        deleted: false
      };

      setState({ stockAudits: [auditEntry, ...stockAudits] });
      saveLocalData();
      syncAfterLocalChange('audit-applied');

      // Clear cached draft
      try {
        localStorage.removeItem('sdr_stock_audit_draft_counts');
      } catch (e) {}

      setDraftStatus('✓ ปรับปรุงยอดสต็อกตามการตรวจนับเรียบร้อยแล้ว');
      setConfirmModalOpen(false);
      alert(`บันทึกการตรวจนับสต็อกสำเร็จ! พบผลต่างและปรับปรุงยอดทั้งหมด ${totalDiscrepancies} รายการ`);
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการปิดยอดการตรวจนับ: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 touch-scroll">
      {/* Header Bar (Original Image 2 layout with Safari WebKit polish) */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
            <ClipboardCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">ระบบตรวจนับและกระทบยอดสต็อก</h2>
            <div className="text-xs text-slate-600 font-semibold">{draftStatus}</div>
          </div>
        </div>

        {/* Location Selector & Action Buttons */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="pl-9 pr-8 py-2 rounded-xl border border-slate-300 bg-white text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 appearance-none transition-all cursor-pointer"
            >
              <option value="all">ทุกสถานที่ / ตู้จัดเก็บ</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold shadow-xs transition-colors cursor-pointer ${
              showHistory
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-white border-slate-300 text-slate-800 hover:bg-slate-50'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            <span>ประวัติ</span>
          </button>

          <button
            onClick={() => setConfirmModalOpen(true)}
            disabled={saving || auditItems.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white text-xs font-bold shadow-xs transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
            <span>ยืนยันและปิดยอด</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {errorMsg && (
        <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* History Panel */}
      {showHistory && (
        <div className="rounded-2xl border border-slate-200/80 bg-white/95 p-4 sm:p-5 shadow-xs animate-in fade-in duration-150">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-600" />
              <span>ประวัติการตรวจนับสต็อก ({stockAudits.length} ครั้งล่าสุด)</span>
            </h3>
            <button
              onClick={() => setShowHistory(false)}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {stockAudits.length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">ยังไม่มีประวัติการบันทึกตรวจนับสต็อก</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto touch-scroll">
              {stockAudits.map((a, idx) => (
                <div key={idx} className="p-3 rounded-xl border border-slate-200 bg-slate-50/70 text-xs flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-900">{formatDateThai(a.date)}</div>
                    <div className="text-slate-500 mt-0.5">
                      ผู้ตรวจนับ: <strong className="text-slate-700">{a.operator || '-'}</strong> • ตำแหน่ง: <span className="text-cyan-700 font-bold">{a.location}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-indigo-600">{a.recordsCount} รายการ</span>
                    <div className={a.discrepanciesCount > 0 ? "text-amber-700 font-bold" : "text-emerald-600 font-semibold"}>
                      ผลต่าง {a.discrepanciesCount} รายการ
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Audit Counting Table (Matching InventoryVirtualTable.jsx styling, columns, and badges) */}
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md shadow-xs overflow-hidden">
        {auditItems.length === 0 ? (
          <div className="p-12 text-center text-slate-500 shadow-xs flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
              <PackageOpen className="w-7 h-7" />
            </div>
            <div className="text-base font-bold text-slate-800">ไม่พบรายการสินค้า</div>
            <div className="text-xs text-slate-500 max-w-sm">
              ไม่พบรายการสินค้าในสถานที่จัดเก็บที่เลือก
            </div>
          </div>
        ) : (
          <div className="table-touch-container">
            <div className="min-w-[700px] lg:min-w-0">
              {/* Table Header (Flex row matching InventoryVirtualTable header) */}
              <div className="flex items-center bg-slate-100/90 border-b border-slate-300/80 px-2 sm:px-3 py-3 text-xs font-extrabold text-slate-800 uppercase tracking-wider select-none">
                {/* Number Index */}
                <div className="w-9 sm:w-10 flex-shrink-0 flex items-center justify-center text-slate-500">
                  #
                </div>

                {/* Product Name */}
                <button
                  onClick={() => handleSort('name')}
                  className="flex-1 min-w-[140px] flex items-center text-left hover:text-slate-900 group cursor-pointer"
                >
                  <span>ชื่อสินค้า / พัสดุ</span>
                  {renderSortIcon('name')}
                </button>

                {/* Brand */}
                <button
                  onClick={() => handleSort('brand')}
                  className="hidden xl:flex items-center w-24 flex-shrink-0 text-left hover:text-slate-900 group cursor-pointer"
                >
                  <span>ยี่ห้อ</span>
                  {renderSortIcon('brand')}
                </button>

                {/* Size / Spec */}
                <button
                  onClick={() => handleSort('size')}
                  className="hidden 2xl:flex items-center w-20 flex-shrink-0 text-left hover:text-slate-900 group cursor-pointer"
                >
                  <span>ขนาด/สเปค</span>
                  {renderSortIcon('size')}
                </button>

                {/* System Quantity */}
                <button
                  onClick={() => handleSort('quantity')}
                  className="w-20 sm:w-24 lg:w-28 flex-shrink-0 flex items-center justify-end text-right hover:text-slate-900 group cursor-pointer pr-2 sm:pr-3"
                >
                  <span>ยอดในระบบ</span>
                  {renderSortIcon('quantity')}
                </button>

                {/* Lots & Expiry (matching Inventory's lot column) */}
                <div className="hidden sm:block w-40 md:w-44 lg:w-56 flex-shrink-0 pr-2 sm:pr-3">
                  <span>ข้อมูลล็อต & วันหมดอายุ</span>
                </div>

                {/* Physical Count Input */}
                <div className="w-32 sm:w-36 lg:w-44 flex-shrink-0 text-center font-extrabold text-slate-800">
                  <span>ยอดนับจริง (Physical)</span>
                </div>

                {/* Difference */}
                <button
                  onClick={() => handleSort('diff')}
                  className="w-18 sm:w-22 lg:w-24 flex-shrink-0 flex items-center justify-end text-right hover:text-slate-900 group cursor-pointer pr-2 sm:pr-3"
                >
                  <span>ผลต่าง (Diff)</span>
                  {renderSortIcon('diff')}
                </button>

                {/* Quick Actions */}
                <div className="w-14 sm:w-16 lg:w-20 flex-shrink-0 text-center pr-1 sm:pr-2">
                  <span>จัดการ</span>
                </div>
              </div>

              {/* Table Rows (Matching InventoryRow.jsx layout and design) */}
              <div className="divide-y divide-slate-100 max-h-[calc(100vh-270px)] overflow-y-auto touch-scroll">
                {auditItems.map((item, index) => {
                  const systemQty = Number(item.quantity) || 0;
                  const physicalQty = counts[item.id] !== undefined ? counts[item.id] : systemQty;
                  const diff = Number((physicalQty - systemQty).toFixed(5));

                  const status = getExpiryStatus(item.expiryDate);
                  const expText = formatExpiryDate(item.expiryDate);
                  const boxSummary = formatBoxContentsSummary(item.boxContents);

                  const statusBadgeColor =
                    status === 'expired'
                      ? 'bg-red-50 text-red-800 border-red-200'
                      : status === 'expiring-soon'
                      ? 'bg-amber-50 text-amber-900 border-amber-200'
                      : 'bg-slate-50 text-slate-800 border-slate-200';

                  return (
                    <div
                      key={item.id}
                      className={`flex items-center transition-colors px-2 sm:px-3 py-2.5 text-xs sm:text-sm select-none ${
                        diff !== 0 ? 'bg-amber-50/25 hover:bg-amber-50/40' : 'bg-white hover:bg-slate-50/80'
                      }`}
                    >
                      {/* Index */}
                      <div className="w-9 sm:w-10 flex-shrink-0 text-center text-xs font-bold text-slate-400">
                        {index + 1}
                      </div>

                      {/* Product Name & Location Badge (Exact matching style as Inventory) */}
                      <div className="flex-1 min-w-[140px] pr-2 sm:pr-3">
                        <div className="font-bold text-slate-950 line-clamp-1">
                          {item.name}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] text-slate-700 font-medium">
                          {item.location && (
                            <span className="inline-flex items-center gap-0.5 text-cyan-800 font-semibold bg-cyan-100/70 border border-cyan-200/80 px-1.5 py-0.5 rounded">
                              <MapPin className="w-3 h-3 text-cyan-700" />
                              <span>{item.location}</span>
                            </span>
                          )}
                          {item.brand && (
                            <span className="xl:hidden text-slate-700 font-medium">
                              <span className="text-slate-500 font-normal">แบรนด์:</span> {item.brand}
                            </span>
                          )}
                          {item.size && (
                            <span className="2xl:hidden text-slate-700 font-medium">
                              <span className="text-slate-500 font-normal">ไซส์:</span> {item.size}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Brand (Desktop) */}
                      <div className="hidden xl:block w-24 flex-shrink-0 text-slate-700 font-medium truncate pr-2">
                        {item.brand || '-'}
                      </div>

                      {/* Size / Spec (Desktop) */}
                      <div className="hidden 2xl:block w-20 flex-shrink-0 text-slate-700 font-medium truncate pr-2">
                        {item.size || '-'}
                      </div>

                      {/* System Quantity (Matches InventoryRow quantity column) */}
                      <div className="w-20 sm:w-24 lg:w-28 flex-shrink-0 text-right pr-2 sm:pr-3 font-extrabold text-indigo-700 tabular-nums text-sm sm:text-base">
                        {systemQty}
                        <span className="text-xs font-semibold text-slate-600 ml-1">
                          {item.unit || 'ชิ้น'}
                        </span>
                      </div>

                      {/* Lots & Expiry Column (Matches InventoryRow lot badge) */}
                      <div className="hidden sm:flex flex-col gap-1 w-40 md:w-44 lg:w-56 flex-shrink-0 pr-2 sm:pr-3">
                        <div className={`flex items-center justify-between gap-1 px-2 py-0.5 rounded text-[11px] border font-semibold ${statusBadgeColor}`}>
                          <span className="font-bold truncate">
                            {item.lot || 'ล็อตหลัก'}
                          </span>
                          <div className="flex items-center gap-1 flex-shrink-0 text-[10px] text-slate-700 font-medium">
                            {expText && <span>{expText}</span>}
                            {boxSummary && (
                              <span className="text-indigo-700 font-bold truncate max-w-[70px]">
                                ({boxSummary})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Physical Count Stepper & Input (Safari WebKit & touch optimized) */}
                      <div className="w-32 sm:w-36 lg:w-44 flex-shrink-0 flex items-center justify-center gap-1 sm:gap-1.5 px-0.5 sm:px-1">
                        <button
                          type="button"
                          onClick={() => handleCountChange(item.id, Math.max(0, physicalQty - 1))}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-bold select-none cursor-pointer text-xs transition-colors"
                          title="ลด 1"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          value={physicalQty}
                          onChange={(e) => handleCountChange(item.id, e.target.value)}
                          className="w-14 sm:w-16 lg:w-20 px-1 sm:px-2 py-1 text-center font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 tabular-nums text-sm sm:text-base no-spinners"
                        />
                        <button
                          type="button"
                          onClick={() => handleCountChange(item.id, physicalQty + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-bold select-none cursor-pointer text-xs transition-colors"
                          title="เพิ่ม 1"
                        >
                          +
                        </button>
                      </div>

                      {/* Difference Indicator */}
                      <div className="w-18 sm:w-22 lg:w-24 flex-shrink-0 text-right pr-2 sm:pr-3 font-extrabold tabular-nums">
                        {diff === 0 ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600 font-bold text-xs sm:text-sm">
                            <Check className="w-3.5 h-3.5" /> 0
                          </span>
                        ) : diff > 0 ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-cyan-50 border border-cyan-200 text-cyan-700 font-bold text-[11px] sm:text-xs">
                            +{diff} (เกิน)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700 font-bold text-[11px] sm:text-xs">
                            {diff} (ขาด)
                          </span>
                        )}
                      </div>

                      {/* Quick Match Action */}
                      <div className="w-14 sm:w-16 lg:w-20 flex-shrink-0 flex items-center justify-center pr-1 sm:pr-2">
                        {diff === 0 ? (
                          <div
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-emerald-600 bg-emerald-50 border border-emerald-200"
                            title="ยอดนับตรงกับระบบแล้ว"
                          >
                            <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleQuickMatch(item.id, systemQty)}
                            title="ปรับยอดนับจริงให้เท่ากับยอดระบบทันที"
                            className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 border border-slate-200 text-slate-700 font-bold text-xs transition-colors cursor-pointer"
                          >
                            ตรง
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal before applying Audit */}
      {confirmModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 shadow-xl border border-slate-200 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-indigo-600">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">ยืนยันการปิดยอดตรวจนับสต็อก</h3>
                <p className="text-xs text-slate-500 font-medium">บันทึกและปรับปรุงยอดคงเหลือในคลัง</p>
              </div>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-2">
              <div className="flex justify-between text-slate-700">
                <span>สถานที่ตรวจนับ:</span>
                <strong className="text-slate-900">{selectedLocation === 'all' ? 'ทุกสถานที่ / ตู้จัดเก็บ' : selectedLocation}</strong>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>จำนวนรายการทั้งหมด:</span>
                <strong className="text-slate-900">{auditItems.length} รายการ</strong>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>รายการที่ยอดตรงกัน:</span>
                <strong className="text-emerald-600">{stats.matchCount} รายการ</strong>
              </div>
              <div className="flex justify-between text-slate-700">
                <span>รายการที่มีผลต่าง (ต้องปรับยอด):</span>
                <strong className={stats.diffCount > 0 ? "text-amber-600 font-extrabold" : "text-slate-900"}>
                  {stats.diffCount} รายการ
                </strong>
              </div>
            </div>

            {stats.diffCount > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200">
                ระบบจะสร้างประวัติการปรับปรุงสต็อก (Adjust) สำหรับ {stats.diffCount} รายการที่มีผลต่างโดยอัตโนมัติ
              </p>
            )}

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setConfirmModalOpen(false)}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-bold transition-all cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleApplyAudit}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs transition-all cursor-pointer"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                <span>{saving ? 'กำลังบันทึก...' : 'ยืนยันและปิดยอด'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
